import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * /ats/activity — rebuilt.
 *
 * The old page listed 9,638 rows of which 88% were "someone opened a page",
 * showed no machine actions at all, and had a single "All Entities" dropdown.
 * Ruhshad's complaint was exact: "I do not see any activity or any activity
 * log for that even if it is automated at the back."
 *
 * This version reads the same collection through /api/activity/feed, which
 * returns one plain-English sentence per row, hides page views by default,
 * and can separate what a PERSON did from what the MACHINE did.
 */

const AREAS = [
  { key: '',           label: 'Everything' },
  { key: 'bde',        label: 'Business development' },
  { key: 'mail',       label: 'Email' },
  { key: 'pipeline',   label: 'Candidates in play' },
  { key: 'candidates', label: 'CVs & records' },
  { key: 'bgv',        label: 'Background checks' },
  { key: 'admin',      label: 'Settings' },
  { key: 'access',     label: 'Sign-ins' },
];

const AREA_STYLE = {
  bde:        { dot: 'bg-amber-500',   chip: 'bg-amber-50 text-amber-800 border-amber-200' },
  mail:       { dot: 'bg-sky-500',     chip: 'bg-sky-50 text-sky-800 border-sky-200' },
  pipeline:   { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  candidates: { dot: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-800 border-violet-200' },
  bgv:        { dot: 'bg-rose-500',    chip: 'bg-rose-50 text-rose-800 border-rose-200' },
  admin:      { dot: 'bg-slate-500',   chip: 'bg-slate-50 text-slate-700 border-slate-200' },
  access:     { dot: 'bg-slate-300',   chip: 'bg-slate-50 text-slate-500 border-slate-200' },
  other:      { dot: 'bg-slate-400',   chip: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const AREA_LABEL = {
  bde: 'Business dev', mail: 'Email', pipeline: 'Pipeline',
  candidates: 'Records', bgv: 'Checks', admin: 'Settings',
  access: 'Sign-in', other: 'Other',
};

function ago(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return m + (m === 1 ? ' min ago' : ' mins ago');
  const h = Math.floor(m / 60);
  if (h < 24) return h + (h === 1 ? ' hour ago' : ' hours ago');
  const d = Math.floor(h / 24);
  if (d < 30) return d + (d === 1 ? ' day ago' : ' days ago');
  return new Date(iso).toLocaleDateString('en-IN',
    { day: 'numeric', month: 'short', year: 'numeric' });
}

function exactTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN',
      { day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit' });
  } catch (e) { return iso; }
}

const api = (path) => {
  const token = localStorage.getItem('ats_token');
  return fetch(path, { headers: token ? { Authorization: 'Bearer ' + token } : {} });
};

export default function ActivityFeedPage() {
  const [events, setEvents] = useState([]);
  const [facets, setFacets] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [area, setArea] = useState('');
  const [who, setWho] = useState('');            // '' | 'person' | 'machine'
  const [showNoise, setShowNoise] = useState(false);
  const [q, setQ] = useState('');
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(100);
  const [openRow, setOpenRow] = useState(null);
  // mobile-round1 (8 Sep 2026): on a phone the eight area chips plus the search,
  // the Everyone/People/machine switch, the date range and the page-views
  // checkbox wrapped into five ragged lines. They now sit behind one Filters
  // button. Desktop markup and layout are unchanged.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const debounce = useRef(null);
  const [queryText, setQueryText] = useState('');
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQ(queryText), 350);
    return () => clearTimeout(debounce.current);
  }, [queryText]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (area) params.set('area', area);
      if (who) params.set('actor', who);
      if (showNoise) params.set('include_noise', 'true');
      if (q) params.set('q', q);
      if (days) params.set('days', String(days));
      const r = await api('/api/activity/feed?' + params.toString());
      if (!r.ok) throw new Error('Could not load the activity log (' + r.status + ')');
      const j = await r.json();
      setEvents(j.events || []);
      setTotal(j.total || 0);
    } catch (e) {
      setError(e.message || 'Something went wrong loading the activity log.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [area, who, showNoise, q, days, limit]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/activity/facets?days=' + (days || 30));
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setFacets(j);
      } catch (e) { /* the summary strip is optional */ }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const counts = facets || {};

  return (
    <div className="page-container max-md:pb-[calc(1.5rem+env(safe-area-inset-bottom))]">

      {/* ---------- heading ---------- */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Activity</h1>
          <p className="text-sm text-gray-500 mt-1">
            Everything that happened — by you, by your team, and by the machine.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50
                     focus:outline-none focus:ring-2 focus:ring-amber-400 max-md:min-h-[44px]">
          Refresh
        </button>
      </div>

      {/* ---------- summary strip ---------- */}
      {facets && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Tile n={counts.business_events} label="Things that happened"
                sub={'in the last ' + (counts.days || 30) + ' days'} />
          <Tile n={counts.by_machine} label="Done by the machine"
                sub="automatic, no one clicked" accent="text-amber-700" />
          <Tile n={counts.by_person} label="Done by people"
                sub="you, Smita, Vaibhavi" />
          <Tile n={counts.noise_events} label="Page views"
                sub="hidden unless you ask" muted />
        </div>
      )}

      {/* ---------- filters ---------- */}
      <button
        type="button"
        onClick={() => setFiltersOpen((v) => !v)}
        className="hidden max-md:flex items-center gap-2 w-full mt-5 min-h-[44px] px-3 rounded-lg
                   border border-gray-200 bg-white text-sm font-medium text-gray-700"
        aria-expanded={filtersOpen}
        data-testid="activity-filters-toggle">
        Filters
        <span className="ml-auto text-xs font-normal text-gray-400">
          {[(AREAS.find((a) => a.key === area) || {}).label,
            who === 'person' ? 'People' : who === 'machine' ? 'The machine' : null,
            q ? 'search' : null].filter(Boolean).join(' · ')}
        </span>
      </button>
      <div className={filtersOpen ? 'contents' : 'contents max-md:hidden'}>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {AREAS.map((a) => (
          <button
            key={a.key || 'all'}
            onClick={() => setArea(a.key)}
            className={
              'px-3 py-1.5 text-sm rounded-full border transition max-md:min-h-[44px] ' +
              (area === a.key
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50')
            }>
            {a.label}
            {facets && a.key && counts.by_area && counts.by_area[a.key] != null && (
              <span className={'ml-2 text-xs ' + (area === a.key ? 'text-gray-300' : 'text-gray-400')}>
                {counts.by_area[a.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          placeholder="Search a name, a company, an action…"
          className="flex-1 min-w-[220px] px-3 py-2 text-sm rounded-lg border border-gray-200
                     focus:outline-none focus:ring-2 focus:ring-amber-400" />

        <Segmented
          value={who}
          onChange={setWho}
          options={[{ v: '', l: 'Everyone' },
                    { v: 'person', l: 'People' },
                    { v: 'machine', l: 'The machine' }]} />

        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white
                     focus:outline-none focus:ring-2 focus:ring-amber-400 max-md:w-full max-md:min-h-[44px]">
          <option value={1}>Today</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 3 months</option>
          <option value={0}>All time</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600 select-none">
          <input type="checkbox" checked={showNoise}
                 onChange={(e) => setShowNoise(e.target.checked)}
                 className="rounded border-gray-300" />
          Show page views
        </label>
      </div>
      </div>

      {/* ---------- the list ---------- */}
      <div className="mt-5 bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading && (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        )}

        {!loading && error && (
          <div className="p-6 text-sm text-rose-700 bg-rose-50 border-b border-rose-100">
            {error}
            <button onClick={load} className="ml-3 underline">Try again</button>
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700 font-medium">Nothing here yet.</p>
            <p className="text-sm text-gray-500 mt-1">
              {area || who || q
                ? 'Nothing matches those filters. Try widening the date range.'
                : 'Once the system starts recording, everything will appear here.'}
            </p>
          </div>
        )}

        {!loading && !error && events.map((e) => {
          const st = AREA_STYLE[e.area] || AREA_STYLE.other;
          const open = openRow === e.id;
          const hasDetail = e.details && Object.keys(e.details).length > 0;
          return (
            <div key={e.id}
                 className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition">
              <button
                onClick={() => setOpenRow(open ? null : e.id)}
                className="w-full text-left px-4 py-3 flex items-start gap-3
                           focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-400">
                <span className={'mt-2 w-2 h-2 rounded-full flex-none ' + st.dot} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-gray-900">{e.text}</span>
                  <span className="mt-1 flex flex-wrap items-center gap-2">
                    <span className={'text-[11px] px-1.5 py-0.5 rounded border ' + st.chip}>
                      {AREA_LABEL[e.area] || 'Other'}
                    </span>
                    {e.actor_kind === 'machine' && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded border
                                       bg-amber-50 text-amber-800 border-amber-200 font-medium">
                        AUTOMATIC
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{e.action}</span>
                  </span>
                </span>
                <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5"
                      title={exactTime(e.at)}>
                  {ago(e.at)}
                </span>
              </button>

              {open && (
                <div className="px-4 pb-4 pl-9 text-xs text-gray-600 space-y-1">
                  <div><span className="text-gray-400">When:</span> {exactTime(e.at)}</div>
                  <div><span className="text-gray-400">Who:</span> {e.who}</div>
                  {e.entity_type && (
                    <div>
                      <span className="text-gray-400">What:</span> {e.entity_type}
                      {e.entity_name ? ' — ' + e.entity_name : ''}
                    </div>
                  )}
                  {hasDetail && (
                    <pre className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded
                                    overflow-x-auto whitespace-pre-wrap break-words">
{JSON.stringify(e.details, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ---------- footer ---------- */}
      {!loading && !error && events.length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
          <span>Showing {events.length} of {total}</span>
          {events.length < total && (
            <button onClick={() => setLimit(limit + 100)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50
                               focus:outline-none focus:ring-2 focus:ring-amber-400 max-md:min-h-[44px]">
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ n, label, sub, accent, muted }) {
  return (
    <div className={'border rounded-xl p-4 ' +
                    (muted ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200')}>
      <div className={'text-2xl font-semibold tabular-nums ' +
                      (accent || (muted ? 'text-gray-400' : 'text-gray-900'))}>
        {n == null ? '—' : n.toLocaleString('en-IN')}
      </div>
      <div className="text-xs font-medium text-gray-600 mt-1">{label}</div>
      <div className="text-xs text-gray-400">{sub}</div>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white max-md:flex max-md:w-full">
      {options.map((o, i) => (
        <button
          key={o.v || 'all'}
          onClick={() => onChange(o.v)}
          className={
            'px-3 py-2 text-sm transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-400 max-md:flex-1 max-md:min-h-[44px] ' +
            (i > 0 ? 'border-l border-gray-200 ' : '') +
            (value === o.v ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50')
          }>
          {o.l}
        </button>
      ))}
    </div>
  );
}
